import React, { useState, useEffect } from 'react';

const KolegaLoadingOverlay = ({ isVisible, message }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isVisible) {
      setProgress(0);
      return;
    }

    // Smooth progress bar animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const increment = Math.random() * 2 + 0.5; // Random increment between 0.5-2.5
        const newProgress = prev + increment;
        return newProgress >= 95 ? 95 : newProgress; // Cap at 95% until completion
      });
    }, 200);

    return () => {
      clearInterval(progressInterval);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <>
      <style>{`
        @keyframes kolegaCodePulse {
          0%, 100% { transform: scale(1) rotate(0deg); filter: drop-shadow(0 0 10px #ADDD30) drop-shadow(0 0 20px #ADDD30); }
          50% { transform: scale(1.1) rotate(5deg); filter: drop-shadow(0 0 20px #C4F03E) drop-shadow(0 0 40px #ADDD30); }
        }
        @keyframes kolegaNeonFlicker {
          0%, 100% { opacity: 1; }
          92% { opacity: 1; }
          93% { opacity: 0.8; }
          94% { opacity: 1; }
          95% { opacity: 0.9; }
          96% { opacity: 1; }
        }
        @keyframes kolegaBracketSlide {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(-2px); }
        }
        @keyframes kolegaSlashRotate {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(10deg); }
        }
        @keyframes progressGlow {
          0%, 100% { box-shadow: 0 0 10px rgba(173, 221, 48, 0.5); }
          50% { box-shadow: 0 0 20px rgba(173, 221, 48, 0.8); }
        }
        .kolega-icon-container {
          animation: kolegaCodePulse 3s ease-in-out infinite, kolegaNeonFlicker 8s linear infinite;
        }
        .kolega-icon-bracket-left { animation: kolegaBracketSlide 2s ease-in-out infinite reverse; }
        .kolega-icon-bracket-right { animation: kolegaBracketSlide 2s ease-in-out infinite; }
        .kolega-icon-slash { transform-origin: center; animation: kolegaSlashRotate 2s ease-in-out infinite; }
        .progress-bar {
          transition: width 0.3s ease-out;
          animation: progressGlow 2s ease-in-out infinite;
        }
      `}</style>
      
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-slate-300 dark:border-neutral-700">
          
          {/* KolegaStudio Brand */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center">
              {/* Animated Code Icon */}
              <div className="relative mr-3">
                <div className="kolega-icon-container">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      className="kolega-icon-bracket-left"
                      d="M7 8L3 12L7 16"
                      stroke="#ADDD30"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      className="kolega-icon-bracket-right"
                      d="M17 8L21 12L17 16"
                      stroke="#ADDD30"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      className="kolega-icon-slash"
                      d="M10 20L14 4"
                      stroke="#ADDD30"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
              
              {/* Brand Text */}
              <h2 className="text-2xl font-bold text-ui-text-primary">
                <span className="mr-1">Kolega</span>
                <span className="font-light">Studio</span>
              </h2>
            </div>
          </div>

          {/* Loading Animation */}
          <div className="flex justify-center mb-6">
            <div className="flex space-x-1">
              <div className="w-3 h-3 bg-brand-action rounded-full animate-bounce"></div>
              <div className="w-3 h-3 bg-brand-action rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-3 h-3 bg-brand-action rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>

          {/* Static Message */}
          <div className="text-center mb-6">
            <p className="text-ui-text-secondary text-lg font-medium">
              Please wait while we set up your project...
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="w-full bg-slate-200 dark:bg-neutral-800 rounded-full h-3 overflow-hidden">
              <div 
                className="progress-bar bg-brand-action h-3 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-ui-text-tertiary mt-1">
              <span>{Math.round(progress)}%</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default KolegaLoadingOverlay;
